import React, { useEffect, useState } from "react";

import CertifBlock from "../../../../components/CertifBlock/CertifBlock";
import DstButton from "../../../../components/DstButton/DstButton";
import { PermissionComponent } from "../../../../services/PermissionGuard";
import { formatMinutesHourly } from "../../../../services/TimeService";
import translate from "../../../../services/Translate";
import UserExams from "../UserExams/UserExams";
import { fetchUserExams } from "../UserExams/UserExams.function";

const UserCertification = ({
  userData,
  userId,
  language,
  certifsLoaded,
  refreshUserCertifications,
  canReadExams,
}: {
  userData: any;
  userId: number;
  language: string;
  certifsLoaded: boolean;
  refreshUserCertifications: Function;
  canReadExams: boolean;
}): JSX.Element => {
  const [filterType, setFilterType] = useState<"all" | "mandatory" | "optional">("all");
  const [exams, setExams] = useState<any[]>([]);
  const [examsLoading, setExamsLoading] = useState(true);
  const refreshUserExams = () => (canReadExams ? fetchUserExams(userId, setExams, setExamsLoading) : Promise.resolve());
  const tabUserData = { ...userData, user_exams: exams };

  useEffect(() => {
    if (canReadExams) {
      fetchUserExams(userId, setExams, setExamsLoading);
    }
  }, [userId, canReadExams]);

  const filteredCertifications =
    userData.user_certifs?.filter((certif: any) => {
      if (["optional", "mandatory"].includes(filterType)) return certif.is_mandatory === (filterType === "mandatory");
      return true;
    }) || [];

  return (
    <React.Fragment>
      <PermissionComponent neededPermission={{ methods: ["GET"], route: "/users/*/certifications" }}>
        <div className="w-100">
          <h2>{translate(language, "PAGES.USER.CERTIFICATION.TITLE")}</h2>
          {certifsLoaded ? (
            <div className="mt-3">
              <DstButton
                variant="info"
                btnClass={`me-2 ${filterType === "all" ? "active-filter-btn" : ""}`}
                clickFunction={() => setFilterType("all")}
                value={`${translate(language, "PAGES.USER.CERTIFICATION.FILTER.ALL")} (${
                  userData.user_certifs?.length || 0
                })`}
              />
              <DstButton
                variant="info"
                btnClass={`me-2 ${filterType === "mandatory" ? "active-filter-btn" : ""}`}
                clickFunction={() => setFilterType("mandatory")}
                value={`${translate(language, "PAGES.USER.CERTIFICATION.FILTER.MANDATORY")} (${
                  userData.user_certifs?.filter((certif: any) => certif.is_mandatory === true).length || 0
                })`}
              />
              <DstButton
                variant="info"
                btnClass={filterType === "optional" ? "active-filter-btn" : ""}
                clickFunction={() => setFilterType("optional")}
                value={`${translate(language, "PAGES.USER.CERTIFICATION.FILTER.OPTIONAL")} (${
                  userData.user_certifs?.filter(
                    (certif: any) => certif.is_mandatory === false || certif.is_mandatory === null
                  ).length || 0
                })`}
              />
            </div>
          ) : (
            <div className="d-flex">
              <DstButton mock btnWidth="64px" btnClass="me-2" />
              <DstButton mock btnWidth="110px" btnClass="me-2" />
              <DstButton mock btnWidth="90px" />
            </div>
          )}
          <div className="scrollable py-3 mb-3" id="certif-scrollable">
            <div className="d-inline-flex flex-nowrap">
              {certifsLoaded ? (
                filteredCertifications.length > 0 ? (
                  filteredCertifications.map((certif: any, index: number) => (
                    <CertifBlock
                      key={index}
                      attempts={certif.attempt_count}
                      certifDate={certif.dt_created}
                      documentId={certif.document_id}
                      filename={certif.filename}
                      mainText={certif.certification_name}
                      remarkText={certif.duration ? formatMinutesHourly(certif.duration) : ""}
                      topLeftTitle={certif.sprint_name}
                      userFullName={`${userData.first_name} ${userData.last_name}`}
                      refreshUserCertifications={refreshUserCertifications}
                      refreshUserExams={refreshUserExams}
                      userExams={tabUserData.user_exams}
                    />
                  ))
                ) : (
                  <p>{translate(language, "PAGES.USER.CERTIFICATION.NO_CERTIFICATION")}</p>
                )
              ) : (
                [...Array(5)].map((_, index: number) => <CertifBlock mock key={`mock-certif-block-${index}`} />)
              )}
            </div>
          </div>
        </div>
      </PermissionComponent>
      <PermissionComponent neededPermission={{ methods: ["GET"], route: "/users/*/evaluation_attempts" }}>
        <UserExams language={language} user_data={tabUserData} loaded={!examsLoading} />
      </PermissionComponent>
    </React.Fragment>
  );
};

export default UserCertification;
